import {CanActivate, Router, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ConfigService} from '../../core/services/config.service';

/**
 * Schützt die Zubucherseite und alle ihre Unterseiten (additional-booking,
 * additional-booking/:orderId, additional-booking/ordered/:orderIds).
 *
 * Die Funktionalität wird über 'subscription.additionalBooking.enabled' ein- und ausgeschaltet.
 * Welche Produkte zubuchbar sind, steht nicht in der Konfiguration: das ergibt pca aus den
 * optionalen Kombi-Werbeschlüsseln am Werbeschlüssel des jeweiligen Abos.
 *
 * Ist die Funktionalität aus, wird die 404-Seite ausgeliefert: sie soll nicht als "vorhanden,
 * aber gesperrt" erkennbar sein. Bei unklarer Lage (Vorgabe fehlt oder ist nicht ladbar) wird
 * gesperrt, nicht freigegeben.
 *
 * Hinweis: CanActivate läuft VOR dem ConfigResolverService, daher wird die Vorgabe hier
 * via loadConfig() selbst (asynchron, gecacht) geladen statt über das synchrone getConfig().
 */
@Injectable({providedIn: 'root'})
export class AdditionalBookingGuard implements CanActivate {

  /** Schaltet die Zubucher-Funktionalität ein ('true'/'1') und aus. */
  public static readonly ENABLED_CONFIG_KEY = 'subscription.additionalBooking.enabled';

  /**
   * Ziel bei abgeschalteter Funktion. Der Pfad passt auf keine Route und läuft damit in die
   * Wildcard '**' der NotFoundComponent.
   */
  private static readonly NOT_FOUND_PATH = '/not-found';

  constructor(private configService: ConfigService, private router: Router) {
  }

  /**
   * Schaltet der Wert der Vorgabe {@link ENABLED_CONFIG_KEY} die Funktionalität ein? Nur 'true'
   * und '1' (ohne Rücksicht auf Groß-/Kleinschreibung und Leerzeichen); alles andere, auch ein
   * fehlender Wert, schaltet aus. Auch die Navigation (LandingPageService) entscheidet hiernach.
   */
  static isEnabled(value: string): boolean {
    const normalized = value?.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }

  canActivate(): Observable<boolean | UrlTree> {
    return this.configService.loadConfig(AdditionalBookingGuard.ENABLED_CONFIG_KEY).pipe(
      map(config => {
        if (AdditionalBookingGuard.isEnabled(config?.value)) {
          return true;
        }
        return this.router.createUrlTree([AdditionalBookingGuard.NOT_FOUND_PATH]);
      }),
      catchError(error => {
        console.error(`Vorgabe ${AdditionalBookingGuard.ENABLED_CONFIG_KEY} konnte nicht geladen werden`, error);
        return of(this.router.createUrlTree([AdditionalBookingGuard.NOT_FOUND_PATH]));
      }));
  }
}
